import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngxs/store';
import { filter, lastValueFrom, Subject, takeUntil } from 'rxjs';
import { msalConfiguration } from '../../../assets/config/msal-config';
import { AuthState } from '../../core/auth/store/auth.state';
import { SelectClient, SelectProject } from '../../core/auth/store/auth.actions';
import { AuthService } from '../../core/auth/services/auth.service';
import { AuthProject, Organization } from '../../core/auth/models/user';
import { CapabilitiesState } from '../../core/capabilities/capabilities.state';

export interface HeaderTab {
  id: string;
  labelKey: string;
}

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() tabs?: HeaderTab[];
  @Input() activeTab?: string;
  @Output() tabChange = new EventEmitter<string>();

  isSuperadmin = false;
  isClientAdmin = false;
  selectedClient: Organization | undefined;
  selectedProject!: AuthProject;
  selectedProjectId: string = '';
  userProjects: AuthProject[] = [];
  showChangelog = false;
  showUserDropdown = false;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private authService: AuthService,
    private msalService: MsalService
  ) {}

  ngOnInit(): void {
    this.isSuperadmin = this.store.selectSnapshot(AuthState.getAccount).user.superadmin;
    this.isClientAdmin = this.authService.getIsAdmin();
    this.selectedClient = this.store.selectSnapshot(AuthState.getSelectedClient);

    if (this.selectedClient.id !== '') {
      this.clientChanged(this.selectedClient);
      this.selectedProject = this.store.selectSnapshot(AuthState.getSelectedProject);
    }

    this.authService.organizationChanged.pipe(takeUntil(this.destroy$)).subscribe(async () => {
      this.selectedClient = this.store.selectSnapshot(AuthState.getSelectedClient);
      if (this.isSuperadmin || this.authService.getIsClientAdmin(this.selectedClient.id)) {
        this.userProjects = await lastValueFrom(this.authService.getProjectsByClient(this.selectedClient.id));
      } else {
        this.userProjects = this.authService.getUserProjectsByClient(this.selectedClient);
      }
      this.selectedProject = this.store.selectSnapshot(AuthState.getSelectedProject);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** P-10 se ofrece a quien el backend deja administrar perfiles en el proyecto activo. */
  get canAdminProfiles(): boolean {
    return !!this.store.selectSnapshot(CapabilitiesState.canAdminProfiles);
  }

  get userInitials(): string {
    const account = this.store.selectSnapshot(AuthState.getAccount);
    const name = account && account.user ? account.user.name : '';
    if (!name) return '·';
    return name.split(' ').filter(Boolean).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
  }

  toggleUserDropdown(): void {
    this.showUserDropdown = !this.showUserDropdown;
  }

  logout(): void {
    this.msalService.logout({
      postLogoutRedirectUri: msalConfiguration.postLogoutRedirectUri
    });
  }

  openChangelog(): void {
    this.showChangelog = true;
  }

  closeChangelog(): void {
    this.showChangelog = false;
  }

  async clientChanged(client: Organization | null): Promise<void> {
    if (!client) return;
    this.selectedClient = client;
    this.store.dispatch(new SelectClient(client));
    if ((this.isSuperadmin || this.authService.getIsClientAdmin(client.id)) && client.id !== '') {
      this.userProjects = await lastValueFrom(this.authService.getProjectsByClient(client.id));
    } else {
      this.userProjects = this.authService.getUserProjectsByClient(client);
    }
    this.authService.updateOrganizationChanged(true);
  }

  onProjectChanged(project: AuthProject | null): void {
    if (!project) return;
    this.selectedProject = project;
    this.selectedProjectId = project.id;
    this.store.dispatch(new SelectProject(project));
    this.authService.updateProjectChanged(true);
  }

  @HostListener('document:click', ['$event'])
  closeDropdownOnClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target && !target.closest('.header__user')) {
      this.showUserDropdown = false;
    }
  }
}
